import { v4 as uuidv4 } from "uuid";
import { Content } from "@tiptap/react";

import { TSectionPage, TemplatePageType } from "models";
import { defaultCoverPage } from "./default-cover-sheet";

/**
 * Builds the starter document for a BRAND NEW template — used only when a template
 * is first created (loadTemplate's empty-pages branch), never for existing ones.
 *
 * The app ships with no docs/videos, so the template teaches itself: alongside the
 * mandatory cover / contents / defect-list sections, it seeds two guided custom
 * sections using the same starter content as the "Add Section" button — self-cleaning
 * documentation the user edits or removes.
 */

/** Shared "delete me" tip styling — orange, bold for sub-headings, italic for body. */
const TIP_COLOR = "#b06320";
const tipMarks = (bold: boolean): any[] => [
  { type: bold ? "bold" : "italic" },
  { type: "textStyle", attrs: { color: TIP_COLOR } },
]; // Only marks registered in the editor's ExtensionKit — NOT `highlight`, which
// is not in this editor's schema and would fail setContent's content check.

/** An italic orange tip line, no emoji prefix — for body copy under a tip heading. */
const tipLine = (text: string): any => ({
  type: "paragraph",
  attrs: { textAlign: "left" },
  content: [{ type: "text", text, marks: tipMarks(false) }],
});

/** A bold orange sub-heading within the tip block (e.g. "Good for things like:"). */
const tipHeading = (text: string): any => ({
  type: "paragraph",
  attrs: { textAlign: "left" },
  content: [{ type: "text", text, marks: tipMarks(true) }],
});

/** An empty paragraph, for a blank spacer row within the tip block. */
const blank = (): any => ({
  type: "paragraph",
  attrs: { textAlign: "left" },
  content: [],
});

/** A bullet list item styled the same as `tipLine()`, for multi-point guidance. */
const tipBullet = (text: string): any => ({
  type: "listItem",
  content: [
    {
      type: "paragraph",
      attrs: { textAlign: "left" },
      content: [{ type: "text", text, marks: tipMarks(false) }],
    },
  ],
});

const tipList = (items: string[]): any => ({
  type: "bulletList",
  content: items.map(tipBullet),
});

/**
 * Content seeded into every NEW custom section (the "Add Section" button). A short
 * coloured tip block — heading, an example list, and a bulleted rundown of how to
 * work with the section — everything above the fold is disposable guidance,
 * selectable top-to-bottom and deletable in one go once the user starts adding
 * real content. Fresh objects per call (no shared mutable nodes).
 */
export function customSectionStarter(): Content {
  return [
    tipHeading("New custom section — use this space however you like!"),
    blank(),
    tipHeading("Good for things like:"),
    tipLine(
      "Disclaimers, About Us, Terms & Conditions, Scope of Inspection, Health & Safety Notes, or Warranty Information."
    ),
    blank(),
    tipHeading("💡 Tips:"),
    tipList([
      "Drag it in the sidebar to move it",
      "Remove it with the ✕.",
      "Insert any block - text, image, table, signature, and more. Type / to insert a block or select from the right side menu.",
      "Delete this tip and the placeholder text once you're ready to add your own content.",
    ]),
  ];
}

/**
 * Ordered starter sections: cover, a guided custom section, the mandatory contents
 * and defect list, then another guided custom section at the end. Both custom
 * sections use the same starter content as the "Add Section" button (customSectionStarter
 * already returns fresh objects per call, so separate documents/sections never share
 * mutable nodes).
 */
export function seedNewTemplate(): TSectionPage[] {
  return [
    { id: uuidv4(), type: TemplatePageType.coverPage, content: structuredClone(defaultCoverPage) },
    { id: uuidv4(), type: TemplatePageType.customPage, content: customSectionStarter() },
    { id: uuidv4(), type: TemplatePageType.contentsPage, content: [] },
    { id: uuidv4(), type: TemplatePageType.defectList, content: [] },
    { id: uuidv4(), type: TemplatePageType.customPage, content: customSectionStarter() },
  ];
}
